import { AddOutlined } from '@mui/icons-material';
import { Box, Button } from '@mui/material';
import { useQueryClient } from '@tanstack/react-query';
import { useCallback, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router';

import OrganizationSelector from '../../profile/components/organizationSelector';
import AgreementForm from '../components/agreementForm';
import AgreementTable from '../components/agreementTable';
import AgreementViewerModal, { B2BSignPayload } from '../components/agreementViewerModal';
import PageHeader from 'components/v2/common/pageHeader';

import api from 'api';
import { ACTIONS } from 'constant';
import useModalObj from 'hooks/useModalObj';
import { useOrganizationAgents } from 'hooks/useOrganizationAgents';
import {
    organizationAgreementKeys,
    useOrganizationAgreements,
} from 'hooks/useOrganizationAgreements';
import { useOrganizationProfile } from 'hooks/useOrganizationProfile';
import useTranslate from 'hooks/useTranslate';
import { setAlert } from 'redux/alert';
import { RootState } from 'redux/store';
import {
    Agent,
    CompanyAgreementDocumentInterface,
    OrganizationAgreementFormInterface,
    OrganizationAgreementInterface,
} from 'types';
import { getErrorMessage } from 'utils/v2/errorHelper';
import { b2bAgreementFormInitialValues } from 'utils/validations/organization';

const emptyAgents: Agent[] = [];

const OrganizationAgreement = () => {
    const dispatch = useDispatch();
    const { translate } = useTranslate();
    const {
        id,
        agreementEmailOptions,
        agents,
        myAgents,
        orgEmail,
        agreements,
        loading,
        fetchAgreements,
    } = useOrganizationAgreement();
    const user = useSelector((state: RootState) => state.user.entity);

    const { modalObject, setModalObject, onResetModalObj } = useModalObj();
    const myServiceProviderId = useSelector((state: RootState) =>
        String(state.user.entity.service_provider_id ?? '')
    );

    const isSameOrganization = id === myServiceProviderId;

    const handleSignPreview = async (
        agreement: OrganizationAgreementInterface,
        action: ACTIONS
    ) => {
        if (!id || !agreement) {
            return;
        }

        try {
            setModalObject((prev) => ({ ...prev, disabled: true }));
            const data = await api.clientServiceProviders.getOrganizationAgreementResourceUri(
                id,
                String(agreement.id)
            );
            setModalObject((prev) => ({
                ...prev,
                disabled: false,
                action,
                title: 'agreementPreview',
                data: { ...agreement, ...data },
            }));
        } catch {
            // If preview PDF is unavailable but action is SIGN, still open the modal
            // so Party B can sign without a preview
            if (action === ACTIONS.SIGN) {
                setModalObject((prev) => ({
                    ...prev,
                    disabled: false,
                    action,
                    title: 'agreementPreview',
                    data: { ...agreement },
                }));
            } else {
                setModalObject((prev) => ({ ...prev, disabled: false }));
                dispatch(setAlert({ message: 'somethingWentWrong', type: 'error' }));
            }
        }
    };

    const handleSubmit = async (value: OrganizationAgreementFormInterface) => {
        try {
            if (!id) return;

            setModalObject((prev) => ({ ...prev, disabled: true }));

            const selectedAgent = myAgents.find((a) => a.id === value.party_a_agent_id);
            const agentName = selectedAgent
                ? [selectedAgent.firstname, selectedAgent.middlename, selectedAgent.lastname]
                      .filter(Boolean)
                      .join(' ')
                : null;

            const counterPartyAgent = agents.find((a) => a.email === value.party_b_signatory_email);
            const counterPartyName = counterPartyAgent
                ? [
                      counterPartyAgent.firstname,
                      counterPartyAgent.middlename,
                      counterPartyAgent.lastname,
                  ]
                      .filter(Boolean)
                      .join(' ')
                : null;

            const response = await api.clientServiceProviders.createB2BAgreement(
                myServiceProviderId,
                {
                    ...value,
                    client_id: value.client_id ?? Number(id),
                    party_a_signatory_name: value.party_a_signatory_name || agentName,
                    party_a_signatory_title:
                        value.party_a_signatory_title || selectedAgent?.role || null,
                    party_b_signatory_name: counterPartyName,
                }
            );

            if (response.data) {
                dispatch(
                    setAlert({
                        message: 'agreementCreated',
                        type: 'success',
                    })
                );
                fetchAgreements(id);
                onResetModalObj();
            }
        } catch {
            setModalObject((prev) => ({ ...prev, disabled: false }));
            dispatch(setAlert({ message: 'somethingWentWrong', type: 'error' }));
        }
    };

    const onSignAgreement = async (payload: B2BSignPayload) => {
        if (!id) return;
        const agreement = modalObject.data as OrganizationAgreementInterface;
        if (!agreement) return;

        try {
            setModalObject((prev) => ({ ...prev, disabled: true }));
            await api.clientServiceProviders.signB2BAgreement(
                myServiceProviderId,
                agreement.id,
                payload
            );
            dispatch(setAlert({ message: 'agreementSignedSuccessfully', type: 'success' }));
            fetchAgreements(id);
            onResetModalObj();
        } catch (error: unknown) {
            setModalObject((prev) => ({ ...prev, disabled: false }));
            dispatch(
                setAlert({
                    message: getErrorMessage(error, 'somethingWentWrong'),
                    type: 'error',
                })
            );
        }
    };

    const handleAction = (action: ACTIONS, agreement?: OrganizationAgreementInterface) => {
        switch (action) {
            case ACTIONS.CREATE:
                setModalObject((prev) => ({
                    ...prev,
                    action,
                    title: 'createB2bAgreement',
                    data: {
                        ...b2bAgreementFormInitialValues,
                        client_id: Number(id),
                        party_b_signatory_email: orgEmail || null,
                    } as OrganizationAgreementFormInterface,
                }));
                break;
            case ACTIONS.PREVIEW_FILE:
            case ACTIONS.SIGN:
                void handleSignPreview(agreement as OrganizationAgreementInterface, action);
                break;
            default:
                break;
        }
    };

    const displayFile =
        modalObject.action === ACTIONS.SIGN || modalObject.action === ACTIONS.PREVIEW_FILE;
    const showSignButton =
        modalObject.action === ACTIONS.SIGN &&
        (modalObject.data as OrganizationAgreementInterface)?.resource_type ===
            'ORG_TO_ORG_RETAINER_AGREEMENT' &&
        user.service_provider_id ===
            (modalObject.data as OrganizationAgreementInterface)?.client_id &&
        !(modalObject.data as OrganizationAgreementInterface)?.party_b_signed_date;

    return (
        <>
            <Box sx={{ width: '100%', px: { xs: 2, md: 0 } }}>
                <PageHeader title="agreement">
                    <OrganizationSelector />
                    {!isSameOrganization ? (
                        <Button
                            size="small"
                            variant="contained"
                            startIcon={<AddOutlined />}
                            onClick={() => handleAction(ACTIONS.CREATE)}
                        >
                            {translate('createB2bAgreement')}
                        </Button>
                    ) : null}
                </PageHeader>
                <AgreementTable
                    userServiceProviderId={user.service_provider_id}
                    loading={loading}
                    agreements={agreements}
                    handleAction={handleAction}
                />
            </Box>

            {modalObject.action === ACTIONS.CREATE && (
                <AgreementForm
                    initialData={modalObject.data as OrganizationAgreementFormInterface}
                    disabled={modalObject.disabled}
                    title={modalObject.title}
                    agreementEmailOptions={agreementEmailOptions}
                    myAgents={myAgents}
                    onSubmit={handleSubmit}
                    onClose={onResetModalObj}
                />
            )}

            {displayFile && id && (
                <AgreementViewerModal
                    data={(modalObject.data as CompanyAgreementDocumentInterface) ?? undefined}
                    title={modalObject.title}
                    showSignButton={showSignButton}
                    loading={displayFile && !modalObject.data}
                    disabled={modalObject.disabled}
                    agents={myAgents}
                    currentUserName={[user.firstname, user.middlename, user.lastname]
                        .filter(Boolean)
                        .join(' ')}
                    currentUserRole={user.role}
                    onClose={onResetModalObj}
                    onSignAgreement={onSignAgreement}
                />
            )}
        </>
    );
};

export default OrganizationAgreement;

const useOrganizationAgreement = () => {
    const { id } = useParams();
    const queryClient = useQueryClient();
    const myServiceProviderId = useSelector((state: RootState) =>
        String(state.user.entity.service_provider_id ?? '')
    );
    const enabled = Boolean(id && myServiceProviderId);
    const { data: organizationProfile } = useOrganizationProfile(id, { enabled });
    const orgEmail = organizationProfile?.email ?? '';
    const agentsQuery = useOrganizationAgents(id, { enabled });
    const myAgentsQuery = useOrganizationAgents(myServiceProviderId, {
        enabled: enabled && Boolean(myServiceProviderId),
    });
    const agreementsQuery = useOrganizationAgreements(id, { enabled, alertOnError: true });
    const agents = agentsQuery.data ?? emptyAgents;
    const myAgents = myAgentsQuery.data ?? emptyAgents;
    const agreements = agreementsQuery.data ?? [];

    const fetchAgreements = useCallback(
        (organizationId: string) => {
            void queryClient.invalidateQueries({
                queryKey: organizationAgreementKeys.list(organizationId),
            });
        },
        [queryClient]
    );

    const agreementEmailOptions = useMemo(() => {
        const options = agents.map((agent) => {
            const name = [agent.firstname, agent.middlename, agent.lastname]
                .filter(Boolean)
                .join(' ');
            return {
                value: agent.email,
                label: name && agent.email ? `${name} (${agent.email})` : name || agent.email,
            };
        });
        const emailsInList = new Set(agents.map((a) => a.email));
        if (orgEmail && !emailsInList.has(orgEmail)) {
            options.unshift({ value: orgEmail, label: orgEmail });
        }
        return options;
    }, [agents, orgEmail]);

    return {
        id,
        agreementEmailOptions,
        agents,
        myAgents,
        orgEmail,
        agreements,
        loading: agentsQuery.isLoading || myAgentsQuery.isLoading || agreementsQuery.isLoading,
        fetchAgreements,
    };
};
